/**
 * Image
 *
 * @format
 */
import { nanoid } from "nanoid";

import { Frame } from "scenejs";

import ImageTemplate from "../../config/templates/image.json";

/**
 * Retrieves the CSS style object for an image element.
 *
 * @param {Object} element - The image element object.
 * @param {number} element.index - The index of the element.
 * @param {string} element.url - The URL of the image.
 * @returns {Object} The CSS style object for the image element.
 */
export function getImageStyle(element) {
  const exit = () => {
    return new Frame({}).toCSSObject();
  };

  try {
    const data = this.data("~21");

    const spread = this.state?.spread?.number || 0;
    const index = element.index;
    const frame = this.state?.frame?.number || 0;

    if (!data?.spreads[spread]?.elements[index]?.style[frame]) return exit("Missing image.", "~27");

    return new Frame({
      width: "100%",
      height: "100%",
      ...{
        backgroundImage: "url(" + element.url + ")",
        backgroundSize: "cover",
        backgroundPosition: "center center",
        backgroundRepeat: "no-repeat",
      },
    }).toCSSObject();
  } catch (error) {
    console.error(error, "~42");
    return exit();
  }
}

/**
 * Retrieves the URL of an image from the given element.
 *
 * @param {Object} element - The element object containing image data.
 * @param {number} [element.index] - The index of the element.
 * @returns {string|null} The URL of the image or null if not found.
 */
export function getImageUrl(element) {
  try {
    const data = this.data("~51");
    const spread = this.state?.spread?.number || 0;
    const index = element?.index || this.element.selected().index;

    return data?.spreads[spread]?.elements[index]?.url || null;
  } catch (error) {
    return null;
  }
}

/**
 * Retrieves the image ID from the given element.
 *
 * @param {Object} element - The element from which to retrieve the image ID.
 * @param {number} [element.index] - The index of the element.
 * @returns {string|null} The image ID if found, otherwise null.
 */
export function getImageId(element) {
  try {
    const data = this.data("~65");
    const spread = this.state?.spread?.number || 0;
    const index = element?.index || this.element.selected().index;

    return data?.spreads[spread]?.elements[index]?.id || null;
  } catch (error) {
    return null;
  }
}

/**
 * Retrieves the media ID for a given element.
 *
 * @param {Object} element - The element for which to retrieve the media ID.
 * @param {number} [element.index] - The index of the element.
 * @returns {string|null} The media ID of the element, or null if not found.
 */
export function getMediaId(element) {
  try {
    const data = this.data("~79");
    const spread = this.state?.spread?.number || 0;
    const index = element?.index || this.element.selected().index;

    return data?.spreads[spread]?.elements[index]?.mediaId || null;
  } catch (error) {
    return null;
  }
}

/**
 * Adds an image from the provided elements to the workspace.
 *
 * @param {Object} props - The properties for the image to be added.
 * @param {string} props.id - The ID of the image to be added.
 * @param {number} props.top - The top position where the image should be placed.
 * @param {number} props.left - The left position where the image should be placed.
 * @returns {Promise<void>} A promise that resolves when the image has been added.
 *
 * @throws Will throw an error if the image cannot be retrieved or added.
 */
export function addImageFromElements(props) {
  const { t, workspace, errors } = this.props;

  return new Promise(async (resolve, reject) => {
    try {
      const { id, top, left } = props;
      const { media, t } = this.props;
      const image = await media.document.get(id);

      if (!image) {
        errors.warn(t("unexpectedError"), "No matching image found.", "~106");
        return;
      }

      const { width: editorWidth, height: editorHeight } = this.editor.style();
      let width, height, imgTop, imgLeft;

      // For non-background images, set the width and height to a reasonable size
      const maxImageWidth = editorWidth * 0.5 * workspace.zoom.get();
      const maxImageHeight = editorHeight * 0.5 * workspace.zoom.get();
      const minImageWidth = editorWidth * 0.5 * workspace.zoom.get();
      const minImageHeight = editorHeight * 0.5 * workspace.zoom.get();

      // Calculate the aspect ratio of the image
      const imageAspectRatio = image.meta.width / image.meta.height;

      if (imageAspectRatio < 1) {
        // For portrait images, set the height to the maximum height and calculate the width based on the aspect ratio
        height = Math.min(maxImageHeight, Math.max(minImageHeight, image.meta.height));
        width = height * imageAspectRatio;
      } else {
        // For landscape or square images, set the width to the maximum width and calculate the height based on the aspect ratio
        width = Math.min(maxImageWidth, Math.max(minImageWidth, image.meta.width));
        height = width / imageAspectRatio;
      }

      // Center the image horizontally and position it based on the top mouse event
      imgTop = Math.max(50, Math.min(top - height / 2, editorHeight - height - 50));
      imgLeft = Math.max(50, Math.min(left - width / 2, editorWidth - width - 50));

      const described = workspace.languages.get().reduce((acc, language) => {
        acc[language.code] = null;
        return acc;
      }, {});

      // Get the compressed (clco color, compressed) version or url
      const url = image?.variations?.clco || image.url;

      const element = {
        ...ImageTemplate,
        id,
        url,
        described,
        mediaId: image.id,
        variation: "clco",
        style: [
          {
            ...ImageTemplate.style[0],
            width: width * workspace.zoom.get(),
            height: height * workspace.zoom.get(),
            left: imgLeft,
            top: imgTop,
          },
        ],
      };

      this.element.add(element).then(() => {
        // Set the element
        this.element.select({ id });

        resolve();
      });
    } catch (error) {
      errors.error(t("unexpectedError"), error, "~166");
      reject(error);
    }
  });
}

/**
 * Adds an image from the clipboard to the workspace.
 *
 * @param {Object} props - The properties of the image to be added.
 * @param {string} props.id - The ID of the image.
 * @param {number} props.top - The top position of the image.
 * @param {number} props.left - The left position of the image.
 * @param {number} props.width - The width of the image.
 * @param {number} props.height - The height of the image.
 * @param {string} props.url - The URL of the image.
 * @returns {Promise<void>} A promise that resolves when the image is added successfully.
 */
export function addImageFromPaste(props) {
  const { t, workspace, errors } = this.props;

  return new Promise((resolve, reject) => {
    try {
      const { id, top, left, width, height, url } = props;
      const { width: editorWidth, height: editorHeight } = this.editor.style();

      let imgWidth, imgHeight, imgTop, imgLeft;

      // For non-background images, set the width and height to a reasonable size
      const maxImageWidth = editorWidth * 0.75;
      const maxImageHeight = editorHeight * 0.75;
      const minImageWidth = editorWidth * 1.25;
      const minImageHeight = editorHeight * 1.25;

      // Calculate the aspect ratio of the image
      const imageAspectRatio = width / height;

      if (imageAspectRatio < 1) {
        // For portrait images, set the height to the maximum height and calculate the width based on the aspect ratio
        imgHeight = Math.min(maxImageHeight, Math.max(minImageHeight, height));
        imgWidth = imgHeight * imageAspectRatio;
      } else {
        // For landscape or square images, set the width to the maximum width and calculate the height based on the aspect ratio
        imgWidth = Math.min(maxImageWidth, Math.max(minImageWidth, width));
        imgHeight = imgWidth / imageAspectRatio;
      }

      // Center the image horizontally and position it based on the top mouse event
      imgTop = Math.max(50, Math.min(top - height / 2, editorHeight - height - 50));
      imgLeft = Math.max(50, Math.min(left - width / 2, editorWidth - width - 50));

      const described = workspace.languages.get().reduce((acc, language) => {
        acc[language.code] = null;
        return acc;
      }, {});

      const element = {
        ...ImageTemplate,
        id: nanoid(),
        mediaId: id,
        described: described,
        url: url,
        style: [
          {
            ...ImageTemplate.style[0],
            width: imgWidth * workspace.zoom.get(),
            height: imgHeight * workspace.zoom.get(),
            left: imgLeft,
            top: imgTop,
          },
        ],
      };

      this.element.add(element).then(() => {
        resolve();
      });
    } catch (error) {
      errors.error(t("unexpectedError"), error, "~236");
      reject(error);
    }
  });
}

/**
 * Sets the image to a compressed transparent version (clcotr) for a specified element within a spread.
 *
 * @param {Object} params - The parameters for setting the transparent image.
 * @param {string} params.id - The ID of the element to update.
 * @param {string} params.spread - The spread identifier where the element is located.
 * @param {string} params.url - The URL of the transparent image.
 * @returns {Promise<void>} A promise that resolves when the image is successfully updated.
 * @throws Will throw an error if the elements or the specified element is missing.
 */
export function setTransparentImage({ id, spread, url }) {
  return new Promise((resolve, reject) => {
    try {
      const data = this.data("~249");

      if (!data?.spreads[spread]?.elements) throw "Elements are missing.";

      // Find the index of the element with the specified id
      const index = data.spreads[spread].elements.findIndex((el) => el.id === id);

      if (index === -1) throw "Element is missing.";

      // Set the image to the compressed transparent (clcotr)
      data.spreads[spread].elements[index].url = url;
      data.spreads[spread].elements[index].variation = "clcotr";

      this.project
        .update(
          {
            data: data,
            note: "Converted image to transparent.",
            ai: 0,
          },
          "~269"
        )
        .then(() => {
          resolve();
        })
        .catch((error) => {
          throw error;
        });
    } catch (error) {
      console.error(error);
      return reject(error);
    }
  });
}

/**
 * Retrieves a transparent image status for a given element.
 *
 * @param {Object} element - The element for which to get the transparent image status.
 * @param {number} [element.index] - The index of the element.
 * @returns {Promise<boolean>} - A promise that resolves to a boolean indicating whether the image has a transparent variation.
 */
export async function getTransparentImage(element) {
  try {
    const data = this.data("~288");
    const spread = this.state?.spread?.number || 0;
    const index = element?.index || this.element.selected().index;
    const { media } = this.props;
    const image = await media.get(data?.spreads[spread]?.elements[index]?.mediaId);

    return image?.variations?.clcotr !== undefined;
  } catch (error) {
    return false;
  }
}

/**
 * Checks if the selected image is transparent.
 *
 * This function attempts to determine if the currently selected image
 * is transparent by checking its variation property. It retrieves the
 * image data, spread number, and selected element index, then checks
 * if the variation of the image is "clcotr" (compressed, transparent).
 *
 * @returns {boolean} True if the image is transparent, false otherwise.
 */
export function isTransparentImage() {
  try {
    const data = this.data("~304");
    const spread = this.state?.spread?.number || 0;
    const index = this.element.selected().index;

    // Check if the image is transparent (compressed, transparent)
    return data?.spreads[spread]?.elements[index]?.variation === "clcotr";
  } catch (_) {
    return false;
  }
}

/**
 * Asynchronously removes the transparent image from the current selection.
 *
 * This function retrieves the media associated with the selected element,
 * updates the element's URL and variation to indicate the image has been
 * converted to transparent, and then updates the project with the new data.
 *
 * @async
 * @function removeTransparentImage
 * @returns {Promise<void|boolean>} Returns false if an error occurs.
 */
export async function removeTransparentImage() {
  try {
    const { media } = this.props;

    const data = this.data("~320");
    const spread = this.state?.spread?.number || 0;
    const index = this.element.selected().index;

    const mediaId = data?.spreads[spread]?.elements[index]?.mediaId;

    const image = await media.get(mediaId);

    if (!image?.url) return;

    data.spreads[spread].elements[index].url = image.url;
    data.spreads[spread].elements[index].variation = "clco";

    this.project.update(
      {
        data: data,
        note: "Converted image to transparent.",
        ai: 0,
      },
      "~339"
    );
  } catch (e) {
    console.error(e);
    return false;
  }
}
